import type { ValidationReport } from '../validator/report.ts';

/**
 * Serialises a report into `validation-report.json`.
 *
 * The output carries codes and data, never sentences: it is rendered into a language by whoever
 * displays it. That is what makes a report diffable, pasteable into an issue by someone who does
 * not share your language, and readable by tooling.
 *
 * It also carries no filename — only size and hash — so a report can be shared without revealing
 * what the file was called or where it lived.
 */
export const VALIDATION_REPORT_SCHEMA_VERSION = '1.0.0';

export function toValidationReportJson(report: ValidationReport): Record<string, unknown> {
  return {
    $schema: `https://freexamplayer.com/schema/validation-report/${VALIDATION_REPORT_SCHEMA_VERSION}/validation-report.schema.json`,
    kind: 'validation-report',
    schemaVersion: VALIDATION_REPORT_SCHEMA_VERSION,
    generatedAt: report.generatedAt,
    appVersion: report.appVersion,
    file: { bytes: report.file.bytes, contentHash: report.file.contentHash },
    ...(report.input !== undefined && { input: stripUndefined(report.input) }),
    ok: report.ok,
    counts: report.counts,
    suppressedCount: report.suppressedCount,
    stagesRun: [...report.stagesRun],
    issues: report.issues.map((candidate) => ({
      code: candidate.code,
      severity: candidate.severity,
      stage: candidate.stage,
      path: candidate.path,
      ...(candidate.questionId !== undefined && { questionId: candidate.questionId }),
      ...(candidate.field !== undefined && { field: candidate.field }),
      ...(candidate.expected !== undefined && { expected: candidate.expected }),
      ...(candidate.received !== undefined && { received: candidate.received }),
      ...(Object.keys(candidate.params).length > 0 && { params: candidate.params }),
      ...(candidate.hint !== undefined && { hint: candidate.hint }),
    })),
  };
}

function stripUndefined(value: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined));
}

/** Serialises to a Blob the browser can hand to the user as a download. */
export function toValidationReportBlob(report: ValidationReport): Blob {
  return new Blob([`${JSON.stringify(toValidationReportJson(report), null, 2)}\n`], {
    type: 'application/json',
  });
}
